/*
  Yaalix customisations for the product page.

  1. Personalization field
     Shows / hides the "Personalization" input as the shopper switches between
     the "Plain" and "Custom Text" variant options, keeps a live character
     counter and blocks checkout until the value is valid. Limits are rendered
     by snippets/yaalix-personalization.liquid and come from the product type
     (Keychains 3-5, Desk Accessories 3-15).

  2. Add-on products
     Ticked add-ons (Gift Wrap Cover, Lobster Clasp) are added to the cart with
     a single /cart/add.js call, so they travel with the main product from both
     "Add to cart" and "Buy it now".

     Shopify's dynamic checkout button builds its own one-item checkout and
     ignores anything else on the page, so when an add-on is ticked we take the
     click over: everything goes into the cart first and then the shopper is
     sent to /checkout. With no add-on ticked the native button is left alone.
*/
(function () {
  'use strict';

  if (window.yaalixProductCustomizations) return;
  window.yaalixProductCustomizations = true;

  var FORM_SELECTOR = 'form[data-type="add-to-cart-form"]';
  var FIELD_SELECTOR = '[data-yx-personalization]';
  var ADDONS_SELECTOR = '[data-yx-addons]';
  var DYNAMIC_CHECKOUT_SELECTOR = '[data-shopify="payment-button"]';

  var busy = false;

  function routeRoot() {
    if (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) {
      return window.Shopify.routes.root;
    }
    return '/';
  }

  function scopeFor(element) {
    return element.closest('product-info') || element.closest('.product') || document;
  }

  /* ---------------------------------------------------------------- helpers */

  function selectedOptionValues(scope) {
    var values = [];

    scope.querySelectorAll('variant-selects input[type="radio"]:checked').forEach(function (input) {
      values.push(String(input.value || '').trim().toLowerCase());
    });

    scope.querySelectorAll('variant-selects select').forEach(function (select) {
      var option = select.options[select.selectedIndex];
      if (!option) return;
      values.push(String(option.value || option.textContent || '').trim().toLowerCase());
    });

    return values;
  }

  function isTriggerSelected(field) {
    var trigger = String(field.dataset.yxTrigger || '').trim().toLowerCase();
    if (!trigger) return false;

    var scope = scopeFor(field);
    if (scope.querySelector('variant-selects')) {
      return selectedOptionValues(scope).indexOf(trigger) !== -1;
    }

    var form = field.closest('form');
    var current = form ? form.querySelector('.product-variant-id') : null;
    if (!current) return false;

    var ids = String(field.dataset.yxCustomVariants || '')
      .split(',')
      .filter(function (id) {
        return id !== '';
      });

    return ids.indexOf(String(current.value).trim()) !== -1;
  }

  function inputOf(field) {
    return field.querySelector('[data-yx-input]');
  }

  function setError(field, message) {
    var target = field.querySelector('[data-yx-error]');
    field.classList.toggle('yx-personalization--error', Boolean(message));
    if (!target) return;
    target.textContent = message || '';
    target.hidden = !message;
  }

  function setFormError(form, message) {
    var host = form.closest('product-form');
    var wrapper = host ? host.querySelector('.product-form__error-message-wrapper') : null;
    var text = host ? host.querySelector('.product-form__error-message') : null;
    if (!wrapper || !text) {
      if (message) console.error('[Yaalix]', message);
      return;
    }
    text.textContent = message || '';
    wrapper.hidden = !message;
  }

  function updateCounter(field) {
    var counter = field.querySelector('[data-yx-counter]');
    var input = inputOf(field);
    if (!counter || !input) return;
    counter.textContent = String(input.value.trim().length) + ' / ' + String(field.dataset.yxMax || '');
  }

  function validate(field) {
    var input = inputOf(field);
    if (!input) return null;

    var min = parseInt(field.dataset.yxMin, 10) || 0;
    var max = parseInt(field.dataset.yxMax, 10) || 0;
    var value = String(input.value || '').trim();

    if (value === '') {
      return field.dataset.yxMsgEmpty || 'Please add your personalisation text.';
    }
    if (min && value.length < min) {
      return (field.dataset.yxMsgMin || 'Please use at least %min% characters.').replace('%min%', String(min));
    }
    if (max && value.length > max) {
      return (field.dataset.yxMsgMax || 'Please use no more than %max% characters.').replace('%max%', String(max));
    }
    return null;
  }

  /* Returns true when the form may continue to the cart / checkout. */
  function checkPersonalization(form) {
    var field = form.querySelector(FIELD_SELECTOR);
    if (!field || field.hasAttribute('hidden')) return true;

    var input = inputOf(field);
    var message = validate(field);

    if (message) {
      setError(field, message);
      if (input) {
        input.focus();
        input.select();
      }
      return false;
    }

    /* Store the trimmed value so no stray spaces reach the order. */
    if (input) input.value = String(input.value).trim();
    setError(field, '');
    updateCounter(field);
    return true;
  }

  /* ------------------------------------------------- show / hide the field */

  function syncFields() {
    document.querySelectorAll(FIELD_SELECTOR).forEach(function (field) {
      var input = inputOf(field);
      if (!input) return;

      var active = isTriggerSelected(field);

      if (active) {
        field.removeAttribute('hidden');
      } else {
        field.setAttribute('hidden', '');
        setError(field, '');
      }

      /* A disabled input is never submitted, so no empty line item property
         is sent when the shopper picks the plain variant. */
      input.disabled = !active;
      input.required = active;

      updateCounter(field);
    });
  }

  /* ----------------------------------------------------------- the add-ons */

  function addonsFor(form) {
    var container = form.querySelector(ADDONS_SELECTOR);
    if (!container) {
      var scope = scopeFor(form);
      container = scope.querySelector ? scope.querySelector(ADDONS_SELECTOR) : null;
    }
    if (!container) return { container: null, items: [] };

    var items = [];
    container.querySelectorAll('input[type="checkbox"][data-yx-addon-variant]').forEach(function (box) {
      if (!box.checked) return;
      items.push({
        id: parseInt(box.dataset.yxAddonVariant, 10),
        title: box.dataset.yxAddonTitle || ''
      });
    });

    return { container: container, items: items };
  }

  function cartVariantIds() {
    return fetch(routeRoot() + 'cart.js', {
      headers: { Accept: 'application/json' },
      cache: 'no-store'
    })
      .then(function (response) {
        return response.ok ? response.json() : null;
      })
      .catch(function () {
        return null;
      })
      .then(function (cart) {
        if (!cart || !cart.items) return [];
        return cart.items.map(function (item) {
          return String(item.variant_id);
        });
      });
  }

  function addonItems(addons, container, alreadyInCart) {
    var parentTitle = (container && container.dataset.yxParentTitle) || '';

    return addons
      .filter(function (addon) {
        /* Add-ons are one per order, so never stack duplicates. */
        return alreadyInCart.indexOf(String(addon.id)) === -1;
      })
      .map(function (addon) {
        return {
          id: addon.id,
          quantity: 1,
          properties: { _added_with: parentTitle }
        };
      });
  }

  function postToCart(items) {
    if (!items.length) return Promise.resolve(null);

    return fetch(routeRoot() + 'cart/add.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json'
      },
      body: JSON.stringify({ items: items })
    }).then(function (response) {
      if (response.ok) return response.json();
      return response
        .json()
        .catch(function () {
          return null;
        })
        .then(function (data) {
          throw new Error((data && (data.description || data.message)) || 'Sorry, we could not add that to your cart.');
        });
    });
  }

  /* Everything the product form would have sent for the main product. */
  function mainItem(form) {
    var idInput = form.querySelector('.product-variant-id') || form.querySelector('[name="id"]');
    if (!idInput || !idInput.value) return null;

    var quantityInput = form.querySelector('[name="quantity"]');
    var quantity = quantityInput ? parseInt(quantityInput.value, 10) : 1;

    var item = {
      id: parseInt(idInput.value, 10),
      quantity: quantity > 0 ? quantity : 1
    };

    var properties = {};
    var hasProperties = false;

    form.querySelectorAll('[name^="properties["]').forEach(function (input) {
      if (input.disabled) return;
      if ((input.type === 'checkbox' || input.type === 'radio') && !input.checked) return;

      var match = input.name.match(/^properties\[(.+)\]$/);
      if (!match) return;

      var value = String(input.value == null ? '' : input.value).trim();
      if (value === '') return;

      properties[match[1]] = value;
      hasProperties = true;
    });

    if (hasProperties) item.properties = properties;

    var sellingPlan = form.querySelector('[name="selling_plan"]');
    if (sellingPlan && sellingPlan.value) item.selling_plan = sellingPlan.value;

    return item;
  }

  function resubmit(form, submitter) {
    form.dataset.yxAddonsDone = 'true';

    if (typeof form.requestSubmit === 'function') {
      if (submitter && form.contains(submitter)) {
        form.requestSubmit(submitter);
      } else {
        form.requestSubmit();
      }
    } else {
      form.dispatchEvent(new Event('submit', { bubbles: true, cancelable: true }));
    }
  }

  /* --------------------------------------------------- "Add to cart" click */

  document.addEventListener(
    'submit',
    function (event) {
      var form = event.target;
      if (!form || typeof form.matches !== 'function' || !form.matches(FORM_SELECTOR)) return;

      var stop = function () {
        event.preventDefault();
        event.stopPropagation();
      };

      if (!checkPersonalization(form)) {
        stop();
        return;
      }

      if (form.dataset.yxAddonsDone === 'true') {
        delete form.dataset.yxAddonsDone;
        return;
      }

      if (busy) {
        stop();
        return;
      }

      var addons = addonsFor(form);
      if (!addons.items.length) return;

      stop();
      busy = true;
      setFormError(form, '');

      var submitter = event.submitter;

      cartVariantIds()
        .then(function (alreadyInCart) {
          return postToCart(addonItems(addons.items, addons.container, alreadyInCart));
        })
        .catch(function (error) {
          /* Never block the main product because an add-on failed. */
          console.error('[Yaalix add-ons]', error);
          return null;
        })
        .then(function () {
          busy = false;
          resubmit(form, submitter);
        });
    },
    true
  );

  /* --------------------------------------------------- "Buy it now" click */

  document.addEventListener(
    'click',
    function (event) {
      var target = event.target;
      if (!target || typeof target.closest !== 'function') return;

      var button = target.closest(DYNAMIC_CHECKOUT_SELECTOR);
      if (!button) return;

      var form = button.closest('form');
      if (!form || !form.matches(FORM_SELECTOR)) return;

      var stop = function () {
        event.preventDefault();
        event.stopPropagation();
        if (typeof event.stopImmediatePropagation === 'function') event.stopImmediatePropagation();
      };

      if (!checkPersonalization(form)) {
        stop();
        return;
      }

      if (busy) {
        stop();
        return;
      }

      var addons = addonsFor(form);
      if (!addons.items.length) return;

      /* Shopify's dynamic checkout would drop the add-ons, so build the order
         through the cart instead and then hand over to checkout. */
      stop();
      busy = true;
      setFormError(form, '');

      var main = mainItem(form);
      if (!main) {
        busy = false;
        return;
      }

      cartVariantIds()
        .then(function (alreadyInCart) {
          return postToCart([main].concat(addonItems(addons.items, addons.container, alreadyInCart)));
        })
        .then(function () {
          window.location.href = routeRoot() + 'checkout';
        })
        .catch(function (error) {
          busy = false;
          setFormError(form, (error && error.message) || 'Sorry, we could not start your checkout.');
        });
    },
    true
  );

  /* ------------------------------------------------------------- listeners */

  document.addEventListener('change', function (event) {
    var target = event.target;
    if (!target || typeof target.closest !== 'function') return;
    if (target.closest('variant-selects') || target.closest('product-info')) syncFields();
  });

  document.addEventListener('input', function (event) {
    var target = event.target;
    if (!target || typeof target.closest !== 'function') return;

    var field = target.closest(FIELD_SELECTOR);
    if (!field || !target.matches('[data-yx-input]')) return;

    updateCounter(field);
    setError(field, '');
  });

  document.addEventListener(
    'blur',
    function (event) {
      var target = event.target;
      if (!target || typeof target.closest !== 'function') return;

      var field = target.closest(FIELD_SELECTOR);
      if (!field || !target.matches('[data-yx-input]') || field.hasAttribute('hidden')) return;

      setError(field, validate(field));
    },
    true
  );

  if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
    subscribe(PUB_SUB_EVENTS.variantChange, function () {
      setTimeout(syncFields, 0);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', syncFields);
  } else {
    syncFields();
  }
})();
